import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button
} from '@mui/material';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StorageIcon from '@mui/icons-material/Storage';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import BalanceIcon from '@mui/icons-material/Balance';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import VisibilityIcon from '@mui/icons-material/Visibility';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    {
        id: 'introduction',
        label: 'ルームシェアに最適な間取りとは？'
    },
    {
        id: 'ideal-layout',
        label: '理想的な間取りの条件'
    },
    {
        id: 'balance',
        label: '共有スペースと個室のバランス'
    },
    {
        id: 'storage',
        label: '収納スペースの確保'
    },
    {
        id: 'sound-smell',
        label: '音・におい対策'
    },
    {
        id: 'inspection',
        label: '内見時のチェックポイント'
    },
    {
        id: 'summary',
        label: 'まとめ'
    }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {/* 目次 */}
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setShowToc(false)}
                                sx={{ ml: 2 }}
                            >
                                閉じる
                            </Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box
                                        sx={{
                                            minWidth: 32,
                                            minHeight: 32,
                                            bgcolor: '#f5f5f5',
                                            color: '#888',
                                            fontWeight: 'bold',
                                            borderRadius: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 1,
                                            fontSize: '1.1rem'
                                        }}
                                    >
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.98rem', textAlign: 'left' }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアに最適な間取りとは？
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    近年、都心部を中心にルームシェアの人気が高まっています。しかし、複数人で快適に暮らすためには、物件選びが何よりも重要です。<b>「広さ」や「家賃」</b>だけでなく、<b>間取りや設備のバランス</b>こそが、長く円満に暮らすためのカギとなります。
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    この記事では、専門家の立場から、<b>快適な暮らしを実現する間取り選びのコツ</b>を詳しく解説します。これからルームシェアを始める方も、現在の住まいに悩みを感じている方も、ぜひ参考にしてみてください。
                </Typography>
                <Box sx={{ my: 3, textAlign: 'center' }}>
                    <img
                        src="/images/blog/living-share_roomshare-layout-tips.jpg"
                        alt="明るい共有リビングでルームメイトたちが食事や会話を楽しみ、快適な空間でリラックスしている様子。白と木目を基調としたインテリアで、温かい雰囲気が漂っている。"
                        style={{ maxWidth: '100%', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
                    />
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: '#555' }}>
                        共有リビングの充実は、ルームシェアの満足度を大きく左右します
                    </Typography>
                </Box>
            </Box>

            {/* 理想的な間取りの条件 */}
            <Box id="ideal-layout" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <HomeWorkIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                    理想的な間取りの条件
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアにぴったりな間取りには、次のようなポイントが求められます。
                </Typography>
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
                    <List>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleOutlineIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary={<b>個室がしっかり分離されている</b>}
                                secondary="プライバシー確保のため、壁やドアで区切られた個室が理想的です"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleOutlineIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary={<b>個室までの動線が分離されている</b>}
                                secondary="いわゆる「振り分け」で、他の部屋を経由しないで個室に行ける間取りが理想的です"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleOutlineIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary={<b>共有スペースが広く使いやすい</b>}
                                secondary="リビングやキッチン、バス・トイレなどの動線がスムーズで、複数人でもストレスなく使える広さ"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleOutlineIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary={<b>収納スペースが十分にある</b>}
                                secondary="個人の荷物だけでなく、共有の備品置き場やシューズボックスなども重要です"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleOutlineIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary={<b>水回りの使い勝手が良い</b>}
                                secondary="複数人で生活するため、洗面台やトイレの数や配置は要チェック"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleOutlineIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary={<b>防音や換気対策が整っている</b>}
                                secondary="生活音やにおいが気になりにくい構造だと、より快適です"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" sx={{ color: '#78909c' }}>
                    特に2LDKや3LDKなど、部屋数が多く、リビングを中心に個室が配置されているタイプは人気です。間取り図だけでなく、写真や内見で実際の広さや使い勝手を確かめましょう。
                </Typography>
            </Box>

            {/* 共有スペースと個室のバランス */}
            <Box id="balance" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <BalanceIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                    共有スペースと個室のバランス
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアでは<b>「みんなで使う場所」と「自分だけの空間」</b>のバランスがとても大切です。理想は、<b>個室でプライベートを確保しつつ</b>、共有スペースで交流しやすい環境です。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    <EventSeatIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                                    個室のポイント
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon>
                                            <MeetingRoomIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="ドアで完全に仕切られている" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <MeetingRoomIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="窓があり、明るさ・換気が確保できる" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <MeetingRoomIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="ベッド・デスクが置ける広さ" />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    <HomeWorkIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                                    共有スペースのポイント
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon>
                                            <EventSeatIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="リビングが十分な広さでテーブルやソファが置ける" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <EventSeatIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="キッチンが広く、調理スペースが複数人でも使いやすい" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <EventSeatIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="バス・トイレが複数あるとさらに快適" />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Box sx={{ my: 3, textAlign: 'center' }}>

                    <Typography variant="caption" display="block" sx={{ mt: 1, color: '#555' }}>
                        使いやすい共有キッチンは交流を生み出すハブにも
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#607d8b', mt: 2 }}>
                    共有スペースがせまいと、生活リズムの違いからストレスが生じやすくなります。逆に、個室が狭すぎるとプライバシーを感じにくくなるので、<b>両方のバランス</b>を意識しましょう。
                </Typography>
            </Box>

            {/* 収納スペースの確保 */}
            <Box id="storage" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <StorageIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                    収納スペースの確保
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェア物件では、<b>個人の荷物</b>はもちろん、<b>掃除道具や調味料などの共有アイテム</b>も意外と多くなります。収納力が足りないと、部屋が散らかりやすく、トラブルの原因にもなりかねません。
                </Typography>
                <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8f9fa', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        <b>チェックすべき収納ポイント</b>
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <StorageIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="各個室にクローゼットや収納棚があるか" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <StorageIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="キッチンや廊下にパントリーや物入れがあるか" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <StorageIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="靴箱や傘立てなど玄関収納が充実しているか" />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" sx={{ color: '#9e9e9e', mb: 1 }}>
                    収納が足りない場合は、<b>突っ張り棚やボックス</b>などで工夫することもできますが、最初から物件選びで十分な収納があると、<b>長く快適に住みやすくなります</b>。
                </Typography>
            </Box>

            {/* 音・におい対策 */}
            <Box id="sound-smell" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <VolumeUpIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                    音・におい対策
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    共同生活では、<b>生活音やにおい</b>が意外とストレスになることも。物件の構造や設備によって、快適さが大きく左右されます。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}><b>音対策</b></Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon>
                                            <VolumeUpIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="個室同士の壁が厚く、隣室の音が響きにくい" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <VolumeUpIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="リビングから個室への音漏れが少ない間取り" />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}><b>におい対策</b></Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon>
                                            <VolumeUpIcon color="primary" sx={{ transform: 'rotate(-90deg)' }} />
                                        </ListItemIcon>
                                        <ListItemText primary="キッチンや洗面所に換気扇が設置されている" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <VolumeUpIcon color="primary" sx={{ transform: 'rotate(-90deg)' }} />
                                        </ListItemIcon>
                                        <ListItemText primary="個室に窓があり、定期的に換気できる" />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Box sx={{ my: 3, textAlign: 'center' }}>
                    <img
                        src="/images/blog/ventilation-window_roomshare-layout-tips.jpg"
                        alt="共有キッチン近くの大きな窓と換気扇で、調理中でも空気がこもらず快適な環境を保っている様子。明るい外光と清潔感のあるインテリアが印象的。"
                        style={{ maxWidth: '100%', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
                    />
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: '#555' }}>
                        換気のしやすさは快適なルームシェアの必須ポイント
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#607d8b' }}>
                    構造上の問題は後から直しにくいので、内見時に「壁をノックして厚さを確認」「隣室や外部の音がどれくらい聞こえるか」などもぜひチェックしましょう。
                </Typography>
            </Box>

            {/* 内見時のチェックポイント */}
            <Box id="inspection" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <SearchIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                    内見時のチェックポイント
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    実際に物件を内見する際は、次のような点を<b>必ずチェック</b>しましょう。
                </Typography>
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                    <List>
                        <ListItem>
                            <ListItemIcon>
                                <ArrowForwardIosIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="個室のドアや鍵がきちんと閉まるか" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <ArrowForwardIosIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="水回り（トイレ・洗面・バス）の数と使いやすさ" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <ArrowForwardIosIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="収納棚や物入れの大きさ" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <ArrowForwardIosIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="窓からの採光や風通し" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <ArrowForwardIosIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="壁や床の厚さ・音漏れの度合い" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <ArrowForwardIosIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="コンセントやインターネット回線の位置" />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" sx={{ color: '#78909c', mb: 1 }}>
                    <b>複数人での滞在をイメージしながら</b>チェックすることで、後々の後悔を減らすことができます。<b>内見は必ず昼間に・複数人で</b>行うのがおすすめです。
                </Typography>
            </Box>

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 4 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h4" component="h2" gutterBottom>
                    快適なルームシェア生活は間取り選びから
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェア向きの物件を選ぶには、<b>個室と共有スペースのバランス</b>や<b>収納・設備・防音</b>など様々な視点が欠かせません。<b>間取り図だけでは分からないことも多いので、実際に内見して生活をシミュレーション</b>することが大切です。
                </Typography>
                <Typography variant="body2" sx={{ color: '#78909c' }}>
                    「ここならみんなが快適に暮らせそう！」と感じられる物件を、ぜひじっくり探してみてください。物件選びさえ上手くいけば、ルームシェア生活はきっと素敵なものになります。
                </Typography>
                <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label="ルームシェア" color="primary" variant="outlined" />
                    <Chip label="間取り" color="primary" variant="outlined" />
                    <Chip label="内見ポイント" color="primary" variant="outlined" />
                    <Chip label="快適な暮らし" color="primary" variant="outlined" />
                </Box>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-layout-tips',
    slug: 'roomshare-layout-tips',
    title: 'ルームシェア向きの間取りとは？快適に暮らすための物件選びのコツ',
    excerpt: '理想的な間取りの条件\n共有スペースと個室のバランス\n収納スペースの確保\n音・におい対策\n内見時のチェックポイント',
    coverImage: '/images/blog/cover-roomshare-layout_roomshare-layout-tips.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。木目と白を基調にした温かみのある空間で、それぞれがリラックスした表情を見せている。',
    publishedAt: '2024-06-18T00:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '間取り', '暮らしの知恵'],
    tags: ['ルームシェア', '間取り選び', '内見', '収納', '生活音対策'],
    author: {
        name: '佐藤 未来',
        avatar: '/images/avatars/satou-mirai.jpg',
        bio: 'シェアハウス運営・コンサル歴10年以上。首都圏を中心に数多くの物件でのルームシェアアドバイスや監修を行う。快適な共同生活の間取り設計とトラブル回避のノウハウに定評がある。',
        profilePicture: '/images/avatars/satou-mirai.jpg'
    },
    viewCount: 1830,
    contentComponent: PostContent
};

export default post;